// Canvas2D toolkit for the procedural mech art.
//
// Look: crisp vector plates with an ink outline, 3-tone cel shading from a
// fixed key light (upper-left, in SCREEN space so rotated limbs stay lit
// consistently), restrained panel lines, neon only on emissive elements.
//
// A Drawer renders the same part code in two passes:
//   'body'  — the visible sprite (emissive elements as dim tinted glass)
//   'glow'  — emissive elements only, in white; every opaque plate erases the
//             glow behind it, so occlusion matches the body exactly.

import { css, mix, shade } from '../../config/palette';
import { COLORS } from '../../config/palette';
import type { MatKey, MechPalette, Pt } from './styles';
import { pickMip, type CutoutPart } from './cutout';

export type Mode = 'body' | 'glow';

/** Unit vector pointing TOWARD the key light (upper-left), screen space. */
export const LIGHT = { x: -0.5547, y: -0.8321 } as const;

/** Visible ink width outside each shape (px at 1×). */
export const SHAPE_INK = 1.1;
/** Silhouette dilation applied after a whole sprite is painted (px at 1×). */
export const SIL_INK = 1.0;
export const INK = css(COLORS.ink);

export interface PlateOpts {
  /** Form-shadow depth on the side away from the light (px). Default 2. */
  sh?: number;
  /** Lit-edge highlight depth (px). Default 1. */
  hl?: number;
  /** Ink width outside the shape (px). Default SHAPE_INK; 0 = no outline. */
  ink?: number;
  /** Corner rounding radius (px). Default 0.5. */
  r?: number;
}

export function canvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.ceil(w));
  c.height = Math.max(1, Math.ceil(h));
  return c;
}

/**
 * 2D context for an art canvas. All art canvases are CPU-backed
 * (willReadFrequently): the builders draw thousands of small paths and read
 * pixels back for validation, which stalls badly on GPU-backed canvases.
 */
export function ctx2d(c: HTMLCanvasElement): CanvasRenderingContext2D {
  const g = c.getContext('2d', { willReadFrequently: true });
  if (!g) throw new Error('Canvas2D unavailable');
  return g;
}

/** Closed polygon path with small rounded corners (radius clamped per corner). */
export function polyPath(pts: readonly Pt[], r = 0.5): Path2D {
  const p = new Path2D();
  const n = pts.length;
  if (n < 3 || r <= 0) {
    p.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < n; i++) p.lineTo(pts[i][0], pts[i][1]);
    p.closePath();
    return p;
  }
  const mid = (a: Pt, b: Pt): Pt => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const start = mid(pts[n - 1], pts[0]);
  p.moveTo(start[0], start[1]);
  for (let i = 0; i < n; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % n];
    const prev = pts[(i - 1 + n) % n];
    const lim = 0.45 * Math.min(Math.hypot(a[0] - prev[0], a[1] - prev[1]), Math.hypot(b[0] - a[0], b[1] - a[1]));
    p.arcTo(a[0], a[1], b[0], b[1], Math.min(r, lim));
  }
  p.closePath();
  return p;
}

export function discPath(cx: number, cy: number, r: number): Path2D {
  const p = new Path2D();
  p.arc(cx, cy, r, 0, Math.PI * 2);
  return p;
}

export function linePath(pts: readonly Pt[]): Path2D {
  const p = new Path2D();
  p.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < pts.length; i++) p.lineTo(pts[i][0], pts[i][1]);
  return p;
}

function shifted(path: Path2D, dx: number, dy: number): Path2D {
  const q = new Path2D();
  q.addPath(path);
  q.addPath(path, { a: 1, b: 0, c: 0, d: 1, e: dx, f: dy });
  return q;
}

interface CssRamp {
  shadow: string;
  base: string;
  light: string;
  edge: string;
}

export class Drawer {
  readonly g: CanvasRenderingContext2D;
  readonly mode: Mode;
  /** Device scale of the target (1 = sheet). shadowBlur ignores transforms, so it is scaled manually. */
  readonly px: number;
  /** Near/far depth: 1 = near side; < 1 darkens (far leg, far fin). */
  depth = 1;
  /** Frame emissive intensity. */
  glowK = 1;
  /** Multiplier on ink widths (large renders use slightly thinner outlines than a plain upscale). */
  inkScale = 1;
  private pal: MechPalette;
  private rot = 0;
  private rotStack: number[] = [];
  private lx: number = LIGHT.x;
  private ly: number = LIGHT.y;
  private ramps = new Map<string, CssRamp>();
  private last: Path2D | null = null;
  private lastMat: MatKey = 'armor';

  constructor(g: CanvasRenderingContext2D, mode: Mode, pal: MechPalette, px = 1) {
    this.g = g;
    this.mode = mode;
    this.pal = pal;
    this.px = px;
    g.lineJoin = 'round';
    g.lineCap = 'round';
  }

  get palette(): MechPalette {
    return this.pal;
  }

  /** Enter a local frame (translate, rotate, optional uniform scale / mirror). */
  push(x: number, y: number, rot: number, sx = 1, sy = 1): void {
    this.g.save();
    this.g.translate(x, y);
    this.g.rotate(rot);
    if (sx !== 1 || sy !== 1) this.g.scale(sx, sy);
    this.rotStack.push(this.rot);
    this.rot += rot;
    this.updateLight(sx, sy);
  }

  pop(): void {
    this.g.restore();
    this.rot = this.rotStack.pop() ?? 0;
    this.updateLight(1, 1);
  }

  /** Draw inside a bone transform. */
  at(xf: { x: number; y: number; rot: number }, fn: () => void): void {
    this.push(xf.x, xf.y, xf.rot);
    fn();
    this.pop();
  }

  private updateLight(sx: number, sy: number): void {
    const c = Math.cos(-this.rot);
    const s = Math.sin(-this.rot);
    this.lx = (LIGHT.x * c - LIGHT.y * s) * Math.sign(sx);
    this.ly = (LIGHT.x * s + LIGHT.y * c) * Math.sign(sy);
  }

  private ramp(mat: MatKey): CssRamp {
    const key = `${mat}:${this.depth}`;
    let r = this.ramps.get(key);
    if (!r) {
      const src = this.pal.mats[mat];
      const d = this.depth;
      const f = (c: number): string => css(d >= 1 ? c : mix(shade(c, d), this.pal.mats.frame.shadow, (1 - d) * 0.35));
      r = { shadow: f(src.shadow), base: f(src.base), light: f(src.light), edge: f(src.edge) };
      this.ramps.set(key, r);
    }
    return r;
  }

  // ── Opaque shapes ──

  plate(pts: readonly Pt[], mat: MatKey, o: PlateOpts = {}): void {
    this.shape(polyPath(pts, o.r ?? 0.5), mat, o);
  }

  /**
   * Painted cutout part with its `pivot` at the local origin, rotated by rot (bone
   * alignment), at offset (x, y). Body pass: the painting (the far side darkened by depth).
   * Glow pass: occludes glows behind it, then adds its emissive mask.
   */
  image(part: CutoutPart, pivot: string, x = 0, y = 0, rot = 0): void {
    const g = this.g;
    const pv = part.pivots[pivot] ?? [0, 0];
    // Effective device scale of the part = transform scale × part scale.
    const tf = g.getTransform();
    const dev = Math.hypot(tf.a, tf.b) * part.s;
    const mip = pickMip(part, dev);
    const k = part.s / mip.f;
    g.save();
    g.translate(x, y);
    g.rotate(rot);
    g.scale(k, k);
    g.translate(-pv[0] * mip.f, -pv[1] * mip.f);
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = 'high';
    if (this.mode === 'glow') {
      g.globalCompositeOperation = 'destination-out';
      g.drawImage(mip.color, 0, 0);
      if (mip.emit) {
        g.globalCompositeOperation = 'source-over';
        g.globalAlpha = Math.max(0, Math.min(1, this.glowK * this.depthGlow()));
        g.drawImage(mip.emit, 0, 0);
      }
    } else {
      if (this.depth < 1) g.filter = `brightness(${this.depth.toFixed(3)}) saturate(0.85)`;
      g.drawImage(mip.color, 0, 0);
    }
    g.restore();
    this.last = null;
  }

  disc(cx: number, cy: number, r: number, mat: MatKey, o: PlateOpts = {}): void {
    this.shape(discPath(cx, cy, r), mat, { sh: Math.min(2, r * 0.5), ...o });
  }

  shape(path: Path2D, mat: MatKey, o: PlateOpts = {}): void {
    const g = this.g;
    const ink = o.ink ?? SHAPE_INK;
    this.last = path;
    this.lastMat = mat;
    if (this.mode === 'glow') {
      // Occlude any glow behind this plate.
      g.save();
      g.globalCompositeOperation = 'destination-out';
      g.fillStyle = '#000';
      g.fill(path);
      if (ink > 0) {
        g.lineWidth = ink * 2 * this.inkScale;
        g.strokeStyle = '#000';
        g.stroke(path);
      }
      g.restore();
      return;
    }
    const r = this.ramp(mat);
    if (ink > 0) {
      g.lineWidth = ink * 2 * this.inkScale;
      g.strokeStyle = INK;
      g.stroke(path);
    }
    g.fillStyle = r.base;
    g.fill(path);
    const sh = o.sh ?? 2;
    const hl = o.hl ?? 1;
    if (sh > 0 || hl > 0) {
      g.save();
      g.clip(path);
      if (sh > 0) {
        g.fillStyle = r.shadow;
        g.fill(shifted(path, this.lx * sh, this.ly * sh), 'evenodd');
      }
      if (hl > 0) {
        g.fillStyle = r.light;
        g.fill(shifted(path, -this.lx * hl, -this.ly * hl), 'evenodd');
      }
      g.restore();
    }
  }

  // ── Surface detail (body pass only, clipped to the last shape) ──

  /** Restrained panel line in the shadow tone of the last plate. */
  panel(pts: readonly Pt[], w = 0.8, closed = false): void {
    if (this.mode !== 'body' || !this.last) return;
    const g = this.g;
    const path = closed ? polyPath(pts, 0) : linePath(pts);
    g.save();
    g.clip(this.last);
    g.strokeStyle = this.ramp(this.lastMat).shadow;
    g.lineWidth = w;
    g.stroke(path);
    g.restore();
  }

  /** Crisp 1 px edge highlight (use on a few top edges only). */
  edge(pts: readonly Pt[], w = 0.9): void {
    if (this.mode !== 'body' || !this.last) return;
    const g = this.g;
    g.save();
    g.clip(this.last);
    g.strokeStyle = this.ramp(this.lastMat).edge;
    g.lineWidth = w;
    g.stroke(linePath(pts));
    g.restore();
  }

  /** Small rivet / bolt head. */
  bolt(x: number, y: number, r = 0.75): void {
    if (this.mode !== 'body') return;
    const g = this.g;
    const rr = this.ramp(this.lastMat);
    g.fillStyle = rr.shadow;
    g.fill(discPath(x + 0.3, y + 0.3, r));
    g.fillStyle = rr.edge;
    g.fill(discPath(x - 0.15, y - 0.15, r * 0.55));
  }

  /** Flat dark hole / vent slot (no shading). */
  slot(pts: readonly Pt[], r = 0.3): void {
    if (this.mode === 'glow') {
      this.shape(polyPath(pts, r), 'frame', { ink: 0 });
      return;
    }
    this.g.fillStyle = INK;
    this.g.fill(polyPath(pts, r));
  }

  // ── Emissive elements ──

  private emissiveBody(k: number): string {
    const i = Math.max(0, Math.min(1, k * this.glowK));
    const hot = mix(this.pal.reactor, 0xffffff, 0.3);
    return css(mix(this.pal.glass, i > 0.5 ? mix(this.pal.reactor, hot, (i - 0.5) * 2) : this.pal.reactor, Math.min(1, i * 1.6) * 0.92));
  }

  private glowStyle(k: number): number {
    const a = Math.max(0, Math.min(1, k * this.glowK * this.depthGlow()));
    const g = this.g;
    g.globalAlpha = a;
    g.shadowColor = 'rgba(255,255,255,0.9)';
    g.shadowBlur = 3.5 * this.px * Math.min(1.6, 0.6 + k * this.glowK * 0.5);
    g.fillStyle = '#fff';
    g.strokeStyle = '#fff';
    return a;
  }

  private depthGlow(): number {
    return this.depth >= 1 ? 1 : 0.55;
  }

  /** Emissive light strip along a polyline. */
  strip(pts: readonly Pt[], w = 1.2, k = 1): void {
    const g = this.g;
    const path = linePath(pts);
    if (this.mode === 'body') {
      g.lineWidth = w + 1.3 * this.inkScale;
      g.strokeStyle = INK;
      g.stroke(path);
      g.lineWidth = w;
      g.strokeStyle = this.emissiveBody(k);
      g.stroke(path);
      return;
    }
    g.save();
    if (this.glowStyle(k) > 0) {
      g.lineWidth = w;
      g.stroke(path);
    }
    g.restore();
  }

  /** Emissive area (visor, lens, core). */
  lamp(path: Path2D, k = 1, ink = 0.7): void {
    const g = this.g;
    if (this.mode === 'body') {
      if (ink > 0) {
        g.lineWidth = ink * 2 * this.inkScale;
        g.strokeStyle = INK;
        g.stroke(path);
      }
      g.fillStyle = this.emissiveBody(k);
      g.fill(path);
      return;
    }
    g.save();
    if (this.glowStyle(k) > 0) g.fill(path);
    g.restore();
  }

  lampPoly(pts: readonly Pt[], k = 1, ink = 0.7): void {
    this.lamp(polyPath(pts, 0.4), k, ink);
  }

  lampDisc(cx: number, cy: number, r: number, k = 1, ink = 0.7): void {
    this.lamp(discPath(cx, cy, r), k, ink);
    // Hot core highlight.
    if (this.mode === 'body') {
      const i = Math.max(0, Math.min(1, k * this.glowK));
      if (i > 0.2) {
        this.g.fillStyle = css(mix(this.pal.reactor, 0xffffff, 0.75), i * 0.85);
        this.g.fill(discPath(cx - r * 0.25, cy - r * 0.25, r * 0.38));
      }
    } else {
      const g = this.g;
      g.save();
      if (this.glowStyle(k * 1.2) > 0) g.fill(discPath(cx, cy, r * 0.5));
      g.restore();
    }
  }

  /** Bright energy line drawn in BOTH passes (sparks, arcs). In the body pass it uses a hot reactor tint. */
  energy(pts: readonly Pt[], w: number, k: number, color?: number): void {
    const g = this.g;
    const path = linePath(pts);
    if (this.mode === 'body') {
      g.save();
      g.globalAlpha = Math.max(0, Math.min(1, k));
      g.lineWidth = w;
      g.strokeStyle = css(color ?? mix(this.pal.reactor, 0xffffff, 0.55));
      g.stroke(path);
      g.restore();
      return;
    }
    g.save();
    g.globalAlpha = Math.max(0, Math.min(1, k));
    g.shadowColor = 'rgba(255,255,255,0.9)';
    g.shadowBlur = 3 * this.px;
    g.lineWidth = w;
    g.strokeStyle = '#fff';
    g.stroke(path);
    g.restore();
  }
}

/**
 * Stamp a painted sprite onto a destination with a uniform silhouette outline:
 * the sprite's alpha is filled with ink and drawn around a small circle, then
 * the sprite on top. `sil` is a scratch canvas at least as large as `src`.
 */
export function stampOutlined(
  dst: CanvasRenderingContext2D,
  dx: number,
  dy: number,
  src: HTMLCanvasElement,
  sil: HTMLCanvasElement,
  radius = SIL_INK,
): void {
  const sg = ctx2d(sil);
  sg.save();
  sg.setTransform(1, 0, 0, 1, 0, 0);
  sg.globalCompositeOperation = 'copy';
  sg.drawImage(src, 0, 0);
  sg.globalCompositeOperation = 'source-in';
  sg.fillStyle = INK;
  sg.fillRect(0, 0, src.width, src.height);
  sg.restore();
  if (radius > 0) {
    const n = radius <= 1.2 ? 12 : Math.ceil(radius * 8);
    for (let k = 0; k < n; k++) {
      const a = (k / n) * Math.PI * 2;
      dst.drawImage(sil, 0, 0, src.width, src.height, dx + Math.cos(a) * radius, dy + Math.sin(a) * radius, src.width, src.height);
    }
  }
  dst.drawImage(src, dx, dy);
}

/** Tinted copy of a (white) canvas: keeps alpha, replaces colour. */
export function tinted(src: HTMLCanvasElement, color: number): HTMLCanvasElement {
  const out = canvas(src.width, src.height);
  const g = ctx2d(out);
  g.drawImage(src, 0, 0);
  g.globalCompositeOperation = 'source-in';
  g.fillStyle = css(color);
  g.fillRect(0, 0, out.width, out.height);
  return out;
}

/** Multiply-tinted copy (like Phaser's tint on a normal sprite). */
export function multiplied(src: HTMLCanvasElement, color: number): HTMLCanvasElement {
  const out = canvas(src.width, src.height);
  const g = ctx2d(out);
  g.drawImage(src, 0, 0);
  g.globalCompositeOperation = 'multiply';
  g.fillStyle = css(color);
  g.fillRect(0, 0, out.width, out.height);
  g.globalCompositeOperation = 'destination-in';
  g.drawImage(src, 0, 0);
  return out;
}
